import axios from 'axios';

import { SourceCamera, SourceConfig } from '../types';

function normalizeServer(server: string): string {
  return server.includes(':') ? server : `${server}:8443`;
}

export const avigilonConfig: SourceConfig = {
  label: 'Avigilon',
  description: 'Import cameras from an Avigilon system',
  credentialFields: [
    {
      name: 'server',
      label: 'Server',
      type: 'text',
      required: true,
      placeholder: 'IP:PORT or FQDN:PORT',
      helpText:
        'The Avigilon Web API must be enabled and reachable from the internet.',
    },
    {
      name: 'username',
      label: 'Username',
      type: 'text',
      required: true,
    },
    {
      name: 'password',
      label: 'Password',
      type: 'password',
      required: true,
    },
  ],
  qualityOptions: [
    { label: 'Low', value: 'low' },
    { label: 'High', value: 'high' },
  ],
  defaultQuality: 'low',

  async fetchCameras(
    credentials: Record<string, string>,
    apiBaseUrl: string
  ): Promise<SourceCamera[]> {
    const server = normalizeServer(credentials.server);

    const { data: cameras } = await axios.post(
      `${apiBaseUrl}/integrations/avigilon/cameras`,
      {
        server,
        user: credentials.username,
        password: credentials.password,
      }
    );

    return cameras
      .sort(
        (
          a: { location?: string; name?: string },
          b: { location?: string; name?: string }
        ) =>
          (a.location ?? '').localeCompare(b.location ?? '') ||
          (a.name ?? '').localeCompare(b.name ?? '')
      )
      .map(
        (camera: {
          id: string;
          name?: string;
          location?: string;
          manufacturer?: string;
          model?: string;
          serial?: string;
        }) => ({
          id: camera.id,
          name: camera.name ?? '',
          location: camera.location ?? '',
          manufacturer: camera.manufacturer,
          model: camera.model,
          serial: camera.serial,
        })
      );
  },

  buildStreamUri(
    camera: SourceCamera,
    credentials: Record<string, string>,
    quality: string,
    apiBaseUrl: string
  ): string {
    const server = normalizeServer(credentials.server);

    const params = new URLSearchParams({
      server,
      user: credentials.username,
      password: credentials.password,
      camera_id: camera.id,
      quality,
    });

    return `${apiBaseUrl}/integrations/avigilon/stream?${params}`;
  },

  buildStreamName(camera: SourceCamera, quality: string): string {
    const parts = [camera.location, camera.name, camera.serial].filter(Boolean);
    return `${parts.join(' - ')} (${quality})`;
  },

  disableTlsVerification: true,
};
